import { cloneDeep, flatten } from "lodash";

export function orderExactByPropertyList<
  T extends Record<string, any>,
  R extends keyof T,
>(data: T[], property: R, sortedOrder: T[R][]): T[] {
  if (data.length !== sortedOrder.length) {
    throw new Error(
      `Data length ${data.length} and sorted order length ${sortedOrder.length} are not the same`,
    );
  }
  const reducer = (agg: Record<T[R], number>, val: T[R], index: number) => {
    agg[val] = index;
    return agg;
  };
  const orderIndexedByProperty = sortedOrder.reduce(
    reducer,
    {} as Record<T[R], number>,
  );
  const sortedArray = [...data];

  data.forEach((datum) => {
    const propertyValue = datum[property];
    const correctIndex = orderIndexedByProperty[propertyValue];
    sortedArray[correctIndex] = datum;
  });

  return sortedArray;
}

export function bucketSortByPredicate<T extends Record<string, any>, V>(
  data: T[],
  sortBy: (datum: T) => V,
  sortedOrder: V[],
): T[][] {
  const valueToIndexMapping = getValueToIndexMapping(sortedOrder);
  const bucketList: T[][] = Array.from(
    { length: sortedOrder.length },
    () => [],
  );

  data.forEach((datum) => {
    const valueOfConcern = sortBy(datum);
    const bucketIndex = valueToIndexMapping.get(valueOfConcern);
    if (typeof bucketIndex !== "number") {
      // TODO: Consider just pushing on the end of the array
      throw new Error(
        `Property ${valueOfConcern} in data not accounted for in sorting array: ${sortedOrder}`,
      );
    }

    bucketList[bucketIndex].push(cloneDeep(datum));
  });

  return bucketList;
}

export function subsortBucketedList<T>(
  bucketedData: T[][],
  compareFn: (a: T, b: T) => number,
): T[] {
  const clonedBucketedData = cloneDeep(bucketedData);
  clonedBucketedData.forEach((bucketList) => {
    bucketList.sort(compareFn);
  });
  return flatten(clonedBucketedData);
}

export function getValueToIndexMapping<T>(order: T[]): Map<T, number> {
  const reducer = (agg: Map<T, number>, val: T, index: number) => {
    agg.set(val, index);
    return agg;
  };
  return order.reduce(reducer, new Map());
}
